import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getSelectedRegistrationNumber } from '../../../../store/selectors/AuthSelectors';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import VatVerificationModal from './VatVerificationModal';
import VatPaymentVerificationModal from './VatPaymentVerificationModal';
import moment from 'moment';

// Import new components
import VatModernHeader from './components/VatModernHeader';
import VatProgressTracker from './components/VatProgressTracker';
import VatStepContent from './components/VatStepContent';
import VatStepNavigation from './components/VatStepNavigation';

import { Button } from 'react-bootstrap';
import {
    CheckCircle2,
    Info,
    Eye,
    Send,
    CreditCard,
    BookOpen
} from 'lucide-react';

//import { toast } from 'react-toastify';

import { toast } from 'sonner';

import { StepEnum } from './constants';

const stepTitles = {
    [StepEnum.INSTRUCTIONS]: "Instruktioner",
    [StepEnum.REVIEW]: "Granska Momsrapporten",
    [StepEnum.SUBMISSION]: "Skicka momsrapport",
    [StepEnum.PAYMENT]: "Betala momsen",
    [StepEnum.RECORD]: "Bokför dragning"
};

const stepOrder = [
    StepEnum.INSTRUCTIONS,
    StepEnum.REVIEW,
    StepEnum.SUBMISSION,
    StepEnum.PAYMENT,
    StepEnum.RECORD
];

const stepIcons = {
    [StepEnum.INSTRUCTIONS]: Info,
    [StepEnum.REVIEW]: Eye,
    [StepEnum.SUBMISSION]: Send,
    [StepEnum.PAYMENT]: CreditCard,
    [StepEnum.RECORD]: BookOpen
};

function VatPeriodStepper() {
    const { companyId, vatPeriodId, fiscalYearId } = useParams();
    const history = useHistory();

    // Registration number from Redux selector (single source of truth,
    // Session-B Redux extras). Replaces a localStorage JSON.parse hop —
    // see 2026-05-14 post-merge frontend-arch review.
    const companyOrgNr = useSelector(getSelectedRegistrationNumber);

    // State management (matching current implementation)
    const [vatPeriod, setVatPeriod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [undoingClose, setUndoingClose] = useState(false);
    const [currentStep, setCurrentStep] = useState(StepEnum.INSTRUCTIONS);
    const [viewingStep, setViewingStep] = useState(null); // null = viewing currentStep
    const [showModal, setShowModal] = useState(false);
    const [showVatModal, setShowVatModal] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [vatData, setVatData] = useState(null);
    const [totalVat, setTotalVat] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [totalDebit, setTotalDebit] = useState(0);
    const [totalCredit, setTotalCredit] = useState(0);
    const [operationInProgress, setOperationInProgress] = useState(false);

    // Verification tracking state
    const [reviewVerification, setReviewVerification] = useState(null);
    const [recordVerification, setRecordVerification] = useState(null);
    const [paymentMadeDate, setPaymentMadeDate] = useState(null);
    const [showPaymentDateModal, setShowPaymentDateModal] = useState(false);
    const [verificationModalType, setVerificationModalType] = useState(null); // 'review' or 'record'
    const [pendingRecordDate, setPendingRecordDate] = useState(null);

    // Step completion tracking state
    const [completionStatus, setCompletionStatus] = useState({
        instructions_completed: false,
        review_completed: false,
        submission_completed: false,
        payment_completed: false,
        record_completed: false
    });

    const fetchVatPeriodDetails = async () => {
        try {
            const response = await axios.get(getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`), { withCredentials: true });
            if (response.data.success) {
                setVatPeriod(response.data.period);
                setCurrentStep(response.data.period.current_step);
            } else {
                setError('Failed to load VAT period details');
            }
        } catch (err) {
            setError('Error fetching VAT period details');
        } finally {
            setLoading(false);
        }
    };

    // Data fetching (identical to current implementation)
    useEffect(() => {
        fetchVatPeriodDetails();
    }, [companyId, vatPeriodId]);

    useEffect(() => {
        if (vatPeriod) {
            fetchVATCalculations();
        }
    }, [vatPeriod]);

    // Check for existing verifications when step changes or on mount
    useEffect(() => {
        const checkExistingVerifications = async () => {
            if (!vatPeriod || !vatPeriodId) return;

            try {
                // Get completion status for all steps
                const statusResponse = await axios.get(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/completion-status`),
                    { withCredentials: true }
                );

                if (statusResponse.data.success) {
                    const status = statusResponse.data.status;
                    setReviewVerification(status.review_verification);
                    setRecordVerification(status.record_verification);
                    setPaymentMadeDate(status.payment_date);

                    // Set completion status from database
                    setCompletionStatus({
                        instructions_completed: status.instructions_completed || false,
                        review_completed: status.review_completed || false,
                        submission_completed: status.submission_completed || false,
                        payment_completed: status.payment_completed || false,
                        record_completed: status.record_completed || false
                    });
                }
            } catch (error) {
                console.error('Error checking existing verifications:', error);
            }
        };

        checkExistingVerifications();
    }, [vatPeriod?.current_step, vatPeriodId, companyId]);

    const fetchVATCalculations = async () => {
        try {
            const getVATCalculationsApiUrl = getApiUrl(`${companyId}/reporting/vat-calculation`);
            const response = await axios.get(getVATCalculationsApiUrl, {
                withCredentials: true,
                params: {
                    start_date: vatPeriod.start_date,
                    end_date: vatPeriod.end_date,
                    fiscal_year_id: vatPeriod.fiscal_year_id,
                },
            });

            if (response.data.success) {
                setVatData(response.data.vatData);
                setTotalVat(response.data.totalVat);
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error('Error fetching VAT calculations:', error);
        }
    };

    // Business logic methods (identical to current implementation)
    const handleStepComplete = async (nextStep) => {
        setOperationInProgress(true);
        try {
            const validSteps = ['instructions', 'review', 'submission', 'payment', 'record'];

            if (!validSteps.includes(nextStep)) {
                throw new Error('Invalid step value');
            }

            // Special handling for submission step - generate report
            // Backend reconstructs VAT data from the zeroing verification if needed
            if (nextStep === StepEnum.SUBMISSION) {
                const periodDate = new Date(vatPeriod.end_date);
                const vatReportData = {
                    orgNr: companyOrgNr,
                    period: `${periodDate.getFullYear()}${String(periodDate.getMonth() + 1).padStart(2, '0')}`,
                    vatData: vatData,
                    totalVat: totalVat
                };

                const generateReportResponse = await axios.post(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/generate-report`),
                    { vatReportData: vatReportData },
                    { withCredentials: true }
                );
                if (!generateReportResponse.data.success) {
                    throw new Error('Failed to generate VAT report');
                }
            }

            await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/update-step`),
                { current_step: nextStep },
                { withCredentials: true }
            );

            // Mark the step we're leaving as completed locally
            const previousStepIndex = stepOrder.indexOf(nextStep) - 1;
            if (previousStepIndex >= 0) {
                const previousStep = stepOrder[previousStepIndex];
                setCompletionStatus(prev => ({
                    ...prev,
                    [`${previousStep}_completed`]: true
                }));
            }

            setCurrentStep(nextStep);

        } catch (err) {
            const message = err.response?.data?.message || err.message || 'Error updating current step';
            // Show user-friendly message for common errors
            if (message.includes('must be closed') || message.includes('måste stängas')) {
                toast.error('Föregående momsperioder måste stängas innan du kan fortsätta. Gå till momsperiodlistan och stäng äldre perioder först.');
            } else {
                toast.error(message);
            }
            console.error('Error in handleStepComplete:', message);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleCompletePeriod = async () => {
        setOperationInProgress(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/close`),
                { fiscalYearId: vatPeriod.fiscal_year_id },
                { withCredentials: true }
            );

            if (response.data.success) {
                setVatPeriod(prevState => ({ ...prevState, state: 'closed' }));
                // Refresh period data to get updated state
                const periodResponse = await axios.get(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`),
                    { withCredentials: true }
                );
                if (periodResponse.data.success) {
                    setVatPeriod(periodResponse.data.period);
                }
            } else {
                setError('Failed to close VAT period');
            }
        } catch (err) {
            setError('Error closing VAT period');
            console.error('Error in handleCompletePeriod:', err);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleVatPeriodClose = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/close`),
                { fiscalYearId: vatPeriod.fiscal_year_id, markAsHandled: true },
                { withCredentials: true }
            );

            if (response.data.success) {
                setVatPeriod(prevState => ({ ...prevState, state: 'closed', current_step: 'record', handled: 1 }));
                setCurrentStep(StepEnum.RECORD);

                toast.success('Momsperioden har stängts', {
                    description: 'Perioden är nu markerad som avslutad'
                });
            } else {
                setError('Failed to close VAT period');
                toast.error('Kunde inte stänga momsperioden');
            }
        } catch (err) {
            setError('Error closing VAT period');
            toast.error('Ett fel uppstod vid stängning av momsperioden');
            console.error('Error closing VAT period:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUndoClose = async () => {
        setUndoingClose(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/undo-close`),
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                setVatPeriod(prevState => ({ ...prevState, state: 'open' }));
                // Stay on current step — user navigates backwards and undoes actions step by step
                setViewingStep(null);
            } else {
                setError('Failed to reopen VAT period');
            }
        } catch (err) {
            setError('Error reopening VAT period');
        } finally {
            setUndoingClose(false);
        }
    };

    // Additional handler methods (from current implementation)
    const handleReviewVatPeriod = () => {
        const getVatPeriodVerificationSuggestion = getApiUrl(`${companyId}/accounting/vat-period/${vatPeriodId}/vat-verification`);
        axios.post(getVatPeriodVerificationSuggestion, {
            fiscalYearId: fiscalYearId || vatPeriod.fiscal_year_id,
            startDate: vatPeriod.start_date,
            endDate: vatPeriod.end_date
        }, {
            withCredentials: true
        })
            .then((response) => {
                setTransactions(response.data.suggestions);
                calculateTotals(response.data.suggestions);
                setVerificationModalType('review');
                setShowVatModal(true);
            })
            .catch((error) => {
                console.error('Error fetching VAT period verification suggestion:', error);
            });
    };

    const calculateTotals = (transactions) => {
        const totalDebit = transactions.reduce((acc, transaction) => acc + parseFloat(transaction.debit || 0), 0);
        const totalCredit = transactions.reduce((acc, transaction) => acc + parseFloat(transaction.credit || 0), 0);
        setTotalDebit(totalDebit);
        setTotalCredit(totalCredit);
    };

    // handlePayment — soft-retired in session 2j (row 60 Phase B cutover).
    // The function called POST /:companyId/templates/<hardcoded UUID>/suggestion
    // against the legacy accountingtemplatescontroller, but no step child in
    // the stepper actually consumed `handlePayment` — `PaymentStep.js` only
    // uses `handlePaymentMade` for the date-marking workflow. So this entire
    // function was dead code, including the hardcoded legacy template UUID.
    // Kept commented for one session in case a forgotten consumer surfaces.
    //
    // const handlePayment = () => {
    //     const generateBookkeepingSuggestion = async (templateId, parameters, userSelectedAccount) => {
    //         try {
    //             const suggestionApiUrl = getApiUrl(`${companyId}/templates/${templateId}/suggestion`);
    //             const response = await axios.post(suggestionApiUrl, { parameters, userSelectedAccount }, { withCredentials: true });
    //             response.data.suggestedTransactions.sort((a, b) => a.account_number - b.account_number);
    //             setTransactions(response.data.suggestedTransactions);
    //             const totalDebit = response.data.suggestedTransactions.reduce((acc, transaction) => acc + parseFloat(transaction.debit || 0), 0);
    //             const totalCredit = response.data.suggestedTransactions.reduce((acc, transaction) => acc + parseFloat(transaction.credit || 0), 0);
    //             setTotalDebit(totalDebit);
    //             setTotalCredit(totalCredit);
    //         } catch (error) {
    //             console.error('Error generating bookkeeping suggestion:', error);
    //         }
    //     };
    //     const paramsForVerification = {
    //         "date": vatPeriod?.end_date || new Date().toISOString().split('T')[0],
    //         "description": `Momsbetalning ${vatPeriod?.start_date} - ${vatPeriod?.end_date}`,
    //         "series": "V",
    //         "parameters": { "amount": totalVat },
    //         "userDefinedAccounts": {}
    //     };
    //     const templateId = "355cbdc1-7a8e-4e29-9961-d764230faa46";
    //     generateBookkeepingSuggestion(templateId, paramsForVerification.parameters, paramsForVerification.userDefinedAccounts);
    //     setShowModal(true);
    // };

    const handleConfirmVatVerification = async () => {
        setVerifying(true);
        setError(null);

        try {
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const date = vatPeriod.end_date; // Verification date = period end per BFL 5 kap 2 §
            const description = `Momsredovisning för perioden: ${vatPeriod.start_date} - ${vatPeriod.end_date}`;
            //const series = 'A';
            const attachments = [];

            const response = await axios.post(createVerificationApiUrl, {
                date,
                description,
                //series,
                transactions,
                attachments,
                reference_data: {  // NEW: Link to VAT period
                    type: 'vat_reporting_period',
                    vat_period_id: vatPeriodId,
                    step: 'review_step'
                }
            }, { withCredentials: true });

            if (response.data.success) {
                // Snapshot the VAT total locally before accounts are zeroed
                // The backend stores it when generating XML, but we need it for the header immediately
                setVatPeriod(prev => prev ? { ...prev, vat_total: totalVat } : prev);

                // Store the created verification info
                setReviewVerification({
                    verification_id: response.data.verificationId,
                    series: response.data.series,
                    number: response.data.verificationNumber
                });

                setShowVatModal(false);
                setTransactions([]);

                // Mark review step as completed
                await markStepComplete('review');

                toast.success("Verifikat skapat och kopplat till momsperioden");
            } else {
                throw new Error('Failed to create verification');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Ett fel uppstod vid skapandet av verifikatet');
            console.error('Error in handleConfirmVatVerification:', error);
        } finally {
            setVerifying(false);
        }
    };

    const handleConfirmVerification = async (paymentDate) => {
        setVerifying(true);
        setError(null);

        try {
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const date = paymentDate || moment(vatPeriod.end_date).format('YYYY-MM-DD');
            const description = `Momsbetalning ${moment(vatPeriod.start_date).format('MMMM YYYY')}`;
            const attachments = [];

            const response = await axios.post(createVerificationApiUrl, {
                date,
                description,
                transactions,
                attachments
            }, { withCredentials: true });

            if (response.data.success) {
                // Verification created successfully
                toast.success("Betalningsverifikat skapat");

                // Mark payment as made with the same date
                await handlePaymentMade(date);

                setShowModal(false);
                setTransactions([]);
            } else {
                setError('Kunde inte skapa verifikatet');
            }
        } catch (error) {
            setError('Ett fel uppstod vid skapandet av verifikatet');
            console.error('Error in handleConfirmVerification:', error);
            toast.error(error.response?.data?.message || 'Ett fel uppstod vid skapandet av verifikatet');
        } finally {
            setVerifying(false);
        }
    };

    const handleDeleteVerification = async (verificationId, step) => {
        const swal = (await import('sweetalert')).default;
        const confirmDelete = await swal({
            title: "Ångra verifikat?",
            text: "Ett återföringsverifikat kommer att skapas som reverserar det ursprungliga verifikatet.",
            icon: "warning",
            buttons: {
                cancel: {
                    text: "Avbryt",
                    value: false,
                    visible: true
                },
                confirm: {
                    text: "Ångra verifikat",
                    value: true,
                    visible: true,
                    className: "btn-danger"
                }
            },
            dangerMode: true
        });

        if (!confirmDelete) return;

        setOperationInProgress(true);

        try {
            const response = await axios.delete(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/verification/${verificationId}/${step}`),
                { withCredentials: true }
            );

            if (response.data.success) {
                // Clear the verification from state
                if (step === 'review') {
                    setReviewVerification(null);
                    // Unmark review step as completed
                    await axios.delete(
                        getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/review`),
                        { withCredentials: true }
                    );
                    setCompletionStatus(prev => ({ ...prev, review_completed: false }));
                } else if (step === 'record') {
                    setRecordVerification(null);
                    // Unmark record step as completed
                    await axios.delete(
                        getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/record`),
                        { withCredentials: true }
                    );
                    setCompletionStatus(prev => ({ ...prev, record_completed: false }));
                }

                toast.success("Återföringsverifikat skapat");
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Kunde inte ångra verifikat';
            toast.error(message);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handlePaymentMade = async (paymentDate) => {
        setOperationInProgress(true);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/payment-made`),
                { payment_date: paymentDate },
                { withCredentials: true }
            );

            if (response.data.success) {
                setPaymentMadeDate(paymentDate);
                setShowPaymentDateModal(false);

                // Mark payment step as completed
                await markStepComplete('payment');

                //const swal = (await import('sweetalert')).default;
                //swal("Framgång", "Betalning registrerad", "success");
                toast.success("Betalning registrerad");
            }
        } catch (error) {
            //const swal = (await import('sweetalert')).default;
            //swal("Fel", "Kunde inte registrera betalning", "error");
            toast.error("Kunde inte registrera betalning");
        } finally {
            setOperationInProgress(false);
        }
    };

    // Payment verification creation removed - users handle payment outside VAT flow
    // Future: Automated agents will create payment verifications automatically

    const handleClearPayment = async () => {
        const swal = (await import('sweetalert')).default;
        const confirmClear = await swal({
            title: "Ångra betalning?",
            text: "Är du säker på att du vill ta bort betalningsdatumet?",
            icon: "warning",
            buttons: {
                cancel: {
                    text: "Avbryt",
                    value: false,
                    visible: true
                },
                confirm: {
                    text: "Ta bort",
                    value: true,
                    visible: true,
                    className: "btn-danger"
                }
            },
            dangerMode: true
        });

        if (!confirmClear) return;

        setOperationInProgress(true);

        try {
            const response = await axios.delete(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/payment-made`),
                { withCredentials: true }
            );

            if (response.data.success) {
                setPaymentMadeDate(null);

                // Unmark payment step as completed
                await axios.delete(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/payment`),
                    { withCredentials: true }
                );
                setCompletionStatus(prev => ({ ...prev, payment_completed: false }));

                //swal("Framgång", "Betalning borttagen", "success");
                toast.success("Betalning borttagen");
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Kunde inte ta bort betalning';
            //swal("Fel", message, "error");
            toast.error(message);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleCreateRecordVerification = async (recordDate) => {
        setOperationInProgress(true);
        try {
            // Fetch account details to get proper account names
            const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts`);
            const accountsResponse = await axios.get(accountsApiUrl, { withCredentials: true });
            const accounts = accountsResponse.data.accounts || accountsResponse.data || [];

            // Find accounts 1630 and 2650
            const account1630 = accounts.find(acc => acc.account_number === '1630');
            const account2650 = accounts.find(acc => acc.account_number === '2650');

            // Prepare record verification transactions
            // When Skatteverket withdraws: Debit 2650 (VAT clearing), Credit 1630 (Tax account)
            if (!account2650 || !account1630) {
                throw new Error('Konto 2650 eller 1630 saknas i kontoplanen');
            }

            // Use the declared VAT total (stored on the period during XML generation)
            // The live totalVat may be zero after the zeroing verification
            const declaredVat = vatPeriod?.vat_total || totalVat || 0;

            // Direction depends on whether paying or receiving refund
            const isPaying = declaredVat >= 0;
            const absAmount = Math.abs(declaredVat);
            const description = isPaying
                ? `Skatteverkets dragning moms ${moment(vatPeriod.start_date).format('MMMM YYYY')}`
                : `Momsåterbäring från Skatteverket ${moment(vatPeriod.start_date).format('MMMM YYYY')}`;

            const recordTransactions = [
                {
                    account_id: account2650.account_id,
                    account_number: '2650',
                    account_name: account2650.account_name,
                    debit: isPaying ? absAmount : 0,
                    credit: isPaying ? 0 : absAmount,
                    transaction_description: description
                },
                {
                    account_id: account1630.account_id,
                    account_number: '1630',
                    account_name: account1630.account_name,
                    debit: isPaying ? 0 : absAmount,
                    credit: isPaying ? absAmount : 0,
                    transaction_description: description
                }
            ];

            const debitTotal = absAmount;
            const creditTotal = absAmount;

            // Set transactions and totals, then open the VAT verification modal
            setTransactions(recordTransactions);
            setTotalDebit(debitTotal);
            setTotalCredit(creditTotal);
            setVerificationModalType('record');
            setPendingRecordDate(recordDate || vatPeriod.due_date);
            setShowVatModal(true);
        } catch (error) {
            console.error('Error fetching accounts:', error);
            toast.error('Kunde inte hämta kontoinformation');
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleConfirmRecordVerification = async () => {
        setVerifying(true);
        setError(null);

        try {
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const date = pendingRecordDate || vatPeriod.due_date;
            const description = `Skattekontouttag moms ${moment(vatPeriod.start_date).format('MMM')}–${moment(vatPeriod.end_date).format('MMM YYYY')}`;

            const response = await axios.post(createVerificationApiUrl, {
                date,
                description,
                transactions,
                attachments: [],
                reference_data: {
                    type: 'vat_reporting_period',
                    vat_period_id: vatPeriodId,
                    step: 'record_step'
                }
            }, { withCredentials: true });

            if (response.data.success) {
                // Store the created verification info
                setRecordVerification({
                    verification_id: response.data.verificationId,
                    series: response.data.series,
                    number: response.data.verificationNumber
                });

                setShowVatModal(false);
                setTransactions([]);

                // Mark record step as completed (this also closes the period)
                await markStepComplete('record');

                // Reload VAT period data to get updated state
                const periodResponse = await axios.get(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`),
                    { withCredentials: true }
                );
                if (periodResponse.data.success) {
                    setVatPeriod(periodResponse.data.period);
                }

                toast.success("Verifikat för dragning skapat och momsperiod slutförd");
            } else {
                setError('Kunde inte skapa verifikatet');
            }
        } catch (error) {
            setError('Ett fel uppstod vid skapandet av verifikatet');
            console.error('Error in handleConfirmRecordVerification:', error);
            toast.error(error.response?.data?.message || 'Ett fel uppstod vid skapandet av verifikatet');
        } finally {
            setVerifying(false);
        }
    };

    // Step completion marking
    const markStepComplete = async (step) => {
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/${step}`),
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                setCompletionStatus(prev => ({
                    ...prev,
                    [`${step}_completed`]: true
                }));
                toast.success(`Steg "${step}" markerat som klart`);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kunde inte markera steg som klart');
        }
    };

    // Step navigation helpers
    const isStepCompleted = (step) => {
        return completionStatus[`${step}_completed`] || false;
    };

    const isStepActive = (step) => {
        return step === currentStep;
    };

    const getCurrentStepIndex = () => {
        return stepOrder.indexOf(currentStep);
    };

    // The furthest step the user has reached (for enabling preview clicks)
    const getFurthestStepIndex = () => {
        for (let i = stepOrder.length - 1; i >= 0; i--) {
            if (completionStatus[`${stepOrder[i]}_completed`]) {
                return i + 1; // Can access the step after the last completed one
            }
        }
        return 0; // Only first step accessible
    };

    const canGoToNextStep = () => {
        const currentIndex = getCurrentStepIndex();

        if (currentIndex >= stepOrder.length - 1) {
            return false;
        }

        // Instructions step is informational — always allow proceeding
        if (currentStep === StepEnum.INSTRUCTIONS) {
            return true;
        }

        // Review step — requires zeroing verification before proceeding
        // This is the industry standard: you must book the momsredovisning before submitting to Skatteverket
        if (currentStep === StepEnum.REVIEW) {
            return !!reviewVerification;
        }

        // Other steps require completion
        return completionStatus[`${currentStep}_completed`] || false;
    };

    const canGoToPreviousStep = () => {
        const currentIndex = getCurrentStepIndex();
        if (currentIndex <= 0) return false;

        // Cannot go back if current step has irreversible actions taken
        if (currentStep === StepEnum.REVIEW && reviewVerification) return false;
        if (currentStep === StepEnum.PAYMENT && paymentMadeDate) return false;
        if (currentStep === StepEnum.RECORD && recordVerification) return false;

        return true;
    };

    const handleStepChange = async (targetStep, direction = 'forward') => {
        setViewingStep(null); // Clear any preview
        if (direction === 'forward') {
            await handleStepComplete(targetStep);
        } else {
            // Persist backward navigation to backend so refresh returns to correct step
            try {
                await axios.post(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/update-step`),
                    { current_step: targetStep },
                    { withCredentials: true }
                );
            } catch (err) {
                console.error('Error persisting backward navigation:', err);
            }
            setCurrentStep(targetStep);
        }
    };

    // Handle click on progress tracker — preview mode (no actual navigation)
    const handleStepClick = (step) => {
        if (step === currentStep) {
            setViewingStep(null); // Return to actual step
        } else {
            setViewingStep(step); // Preview any step
        }
    };

    // The step whose content is currently displayed
    const displayedStep = viewingStep || currentStep;
    const isPreviewing = viewingStep !== null && viewingStep !== currentStep;

    const goToNextStep = () => {
        if (!canGoToNextStep()) {
            // Show appropriate message based on current step
            if (currentStep === StepEnum.REVIEW && !reviewVerification) {
                toast.error("Du måste skapa ett verifikat för momsrapporten innan du fortsätter");
            } else if (currentStep === StepEnum.RECORD && !recordVerification) {
                toast.error("Du måste skapa ett verifikat för dragningen innan du fortsätter");
            }
            return;
        }

        const nextStepIndex = getCurrentStepIndex() + 1;
        const nextStep = stepOrder[nextStepIndex];
        handleStepComplete(nextStep);
    };

    const goToPreviousStep = () => {
        if (!canGoToPreviousStep()) {
            // Show appropriate message based on current step
            if (currentStep === StepEnum.REVIEW && reviewVerification) {
                toast.error("Du måste ta bort verifikatet innan du kan gå tillbaka");
            } else if (currentStep === StepEnum.PAYMENT && paymentMadeDate) {
                toast.error("Du måste ångra betalningsdatumet innan du kan gå tillbaka");
            } else if (currentStep === StepEnum.RECORD && recordVerification) {
                toast.error("Du måste ta bort verifikatet innan du kan gå tillbaka");
            }
            return;
        }

        const previousStepIndex = getCurrentStepIndex() - 1;
        const previousStep = stepOrder[previousStepIndex];
        setCurrentStep(previousStep);
    };

    // Common props to pass to all step components
    const stepProps = {
        vatPeriod,
        currentStep,
        vatData,
        totalVat,
        transactions,
        totalDebit,
        totalCredit,
        companyId,
        vatPeriodId,
        fiscalYearId,
        isPreviewMode: isPreviewing,
        loading,
        error,
        verifying,
        undoingClose,
        handleStepComplete,
        handleVatPeriodClose,
        handleCompletePeriod,
        handleReviewVatPeriod,
        // handlePayment, // soft-retired session 2j — dead code (no step consumer)
        setShowModal,
        setShowVatModal,
        setTransactions,
        setTotalDebit,
        setTotalCredit,
        setError,
        setVerifying,
        goToNextStep,
        goToPreviousStep,
        canGoToNextStep,
        canGoToPreviousStep,
        calculateTotals,
        // Verification tracking props
        reviewVerification,
        recordVerification,
        paymentMadeDate,
        handleDeleteVerification,
        handlePaymentMade,
        handleClearPayment,
        handleCreateRecordVerification,
        operationInProgress,
        companyOrgNr: companyOrgNr,
        // Step completion tracking props
        markStepComplete,
        completionStatus,
        onReportGenerated: fetchVatPeriodDetails
    };

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{
                    height: '400px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    margin: '2rem 0'
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: '3rem', height: '3rem' }}
                    >
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Laddar momsperiod...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="alert alert-danger"
                role="alert"
                style={{
                    borderRadius: '12px',
                    border: '1px solid #f5c6cb',
                    padding: '2rem',
                    margin: '2rem 0'
                }}
            >
                <h4 className="alert-heading">Ett fel uppstod</h4>
                <p className="mb-0">{error}</p>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .vat-stepper-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 1rem;
                }
                
                .vat-content-wrapper {
                    background: #f8fafc;
                    min-height: 100vh;
                    padding: 2rem 0;
                }
                
                /* Custom scrollbar for the container */
                .vat-stepper-container::-webkit-scrollbar {
                    width: 6px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
                
                /* Mobile responsive adjustments */
                @media (max-width: 768px) {
                    .vat-stepper-container {
                        padding: 0 0.5rem;
                    }
                    
                    .vat-content-wrapper {
                        padding: 1rem 0;
                    }
                }
            `}</style>

            <div className="">
                <div className="vat-stepper-container">
                    <VatModernHeader
                        vatPeriod={vatPeriod}
                        totalVat={vatPeriod?.handled ? null : totalVat}
                        undoingClose={undoingClose}
                        handleUndoClose={handleUndoClose}
                        handleVatPeriodClose={handleVatPeriodClose}
                        loading={loading}
                        currentStep={currentStep}
                    />

                    {vatPeriod?.handled ? (
                        <div style={{
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#64748b'
                        }}>
                            <CheckCircle2 size={48} style={{ color: '#22c55e', marginBottom: '1rem' }} />
                            <h4 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>Perioden är markerad som redan hanterad</h4>
                            <p style={{ marginBottom: '1.5rem' }}>
                                Momsen för denna period har hanterats utanför systemet. Inga ytterligare åtgärder behövs.
                            </p>
                            <Button
                                variant="outline-secondary"
                                onClick={() => history.push(`/${companyId}/vat-periods`)}
                            >
                                Tillbaka till momsperioder
                            </Button>
                        </div>
                    ) : (
                        <>
                            <VatProgressTracker
                                currentStep={displayedStep}
                                actualCurrentStep={currentStep}
                                stepOrder={stepOrder}
                                stepTitles={stepTitles}
                                stepIcons={stepIcons}
                                isStepCompleted={isStepCompleted}
                                isStepActive={(step) => step === displayedStep}
                                onStepClick={handleStepClick}
                            />

                            {isPreviewing && (
                                <div
                                    className="d-flex align-items-center justify-content-between px-3 py-2 mb-2"
                                    style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.85rem' }}
                                >
                                    <span style={{ color: '#1d4ed8' }}>
                                        Förhandsvisning av <strong>{stepTitles[viewingStep]}</strong>
                                    </span>
                                    <button
                                        onClick={() => setViewingStep(null)}
                                        style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
                                    >
                                        ← Tillbaka till {stepTitles[currentStep]}
                                    </button>
                                </div>
                            )}

                            <VatStepContent
                                currentStep={displayedStep}
                                stepProps={stepProps}
                            />

                            <VatStepNavigation
                                currentStep={currentStep}
                                stepOrder={stepOrder}
                                onStepChange={handleStepChange}
                                onCompletePeriod={handleCompletePeriod}
                                operationInProgress={operationInProgress}
                                canProceedToNext={canGoToNextStep()}
                                canGoBack={canGoToPreviousStep()}
                                vatPeriod={vatPeriod}
                                recordVerification={recordVerification}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Keep existing modals */}
            <VatVerificationModal
                showModal={showVatModal}
                setShowModal={setShowVatModal}
                transactions={transactions}
                totalDebit={totalDebit}
                totalCredit={totalCredit}
                verifying={verifying}
                error={error}
                handleConfirmVerification={verificationModalType === 'record' ? handleConfirmRecordVerification : handleConfirmVatVerification}
                vatPeriod={vatPeriod}
                modalType={verificationModalType}
                verificationDescription={
                    verificationModalType === 'record'
                        ? `Skattekontouttag moms ${vatPeriod ? moment(vatPeriod.start_date).format('MMM YYYY') : ''}`
                        : `Momsredovisning ${vatPeriod ? moment(vatPeriod.start_date).format('MMM') + '–' + moment(vatPeriod.end_date).format('MMM YYYY') : ''}`
                }
            />

            <VatPaymentVerificationModal
                showModal={showModal}
                setShowModal={setShowModal}
                totalVat={totalVat}
                verifying={verifying}
                error={error}
                transactions={transactions}
                totalDebit={totalDebit}
                totalCredit={totalCredit}
                handleConfirmVerification={handleConfirmVerification}
            />
        </>
    );
}

export default VatPeriodStepper;